import React, { useState, useCallback } from "react";
import "./ContactStyle.scss";
import { Button, Typography, FormControl, Input, MenuItem, Select, FormHelperText, CircularProgress, TextField } from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { US_STATES } from '../../util/Mock';
import { useDispatch, useSelector } from 'react-redux';
import { submitContactForm } from '../../redux/actions/ContactActions';
import { useNavigate } from "react-router-dom";
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

const FormField = ({ label, name, value, onChange, error, children }) => (
  <FormControl variant="standard" error={!!error} fullWidth>
    <label>{label} <span style={{ color: 'red' }}>*</span></label>
    {children || (
      <Input
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete="new-password"
        inputProps={{
          autoComplete: "new-password",
          'data-lpignore': "true",
          'data-form-type': "other",
          'autocorrect': "off",
          'autocapitalize': "off",
          'spellcheck': "false",
          'data-autocompletetype': "none",
          'current-password': "false"
        }}
      />
    )}
    {error && <FormHelperText>{error}</FormHelperText>}
  </FormControl>
);

function Contact() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const previousStep = useSelector((state) => state.wizardLaunch?.previousStep) || '/';
  const handleButtonClick = useCallback(() => {
    navigate(previousStep);
  }, [navigate, previousStep]);

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    state: "",
    zipCode: "",
    phoneNumber: "",
    email: "",
    militaryAffiliation: "",
    bestTimeToReach: "",
  });
  const [errors, setErrors] = useState({});
  // Update handleChange function
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    let hasInvalidChars = false;

    if (name === 'phoneNumber') {
      hasInvalidChars = /[^0-9-]/.test(value);
      if (hasInvalidChars) {
        setErrors(prev => ({
          ...prev,
          phoneNumber: "Only numbers and hyphens are allowed"
        }));
        return;
      }
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length > 10) {
        return; // Don't update if more than 10 digits
      }
      formattedValue = value.slice(0, 12).replace(/[^0-9-]/g, '');
    } else if (name === 'zipCode') {
      const cleaned = value.replace(/\D/g, '');
      formattedValue = cleaned.length > 5 ? `${cleaned.slice(0, 5)}-${cleaned.slice(5)}` : cleaned;
    }
  
    setFormData(prevData => ({ ...prevData, [name]: formattedValue }));
    if (!hasInvalidChars) {
      setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
    }
  }, []);
  
  // Update validateForm function's phone validation
  const validateForm = useCallback(() => {
    const newErrors = {};
    const patterns = {
      zipCode: /^\d{5}(-\d{4})?$/,
      email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    };
 
    if (!formData.firstName.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.state) newErrors.state = "Please select a State";
    if (!formData.zipCode || !patterns.zipCode.test(formData.zipCode)) newErrors.zipCode = "Please enter a valid zip code";
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, '');
      if (digitsOnly.length !== 10) {
        newErrors.phoneNumber = "Phone number must be 10 digits";
      }
    }
    if (!formData.email || !patterns.email.test(formData.email.trim())) newErrors.email = "Please enter a valid email address";
    if (!formData.militaryAffiliation) newErrors.militaryAffiliation = "Please select an option";
    // Add validation for bestTimeToReach
    if (!formData.bestTimeToReach) {
      newErrors.bestTimeToReach = "Please select a date and time";
    } else {
      const selectedDateTime = new Date(formData.bestTimeToReach);
      const currentDateTime = new Date();
      
      if (selectedDateTime <= currentDateTime) {
        newErrors.bestTimeToReach = "Please select a future date and time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      await dispatch(submitContactForm(formData));
    } finally {
      setIsLoading(false);
    }
  };



  return (
    <div className="contactInfo-wrap">
      <div className="heading-sec">
        <Button className="back-btn" onClick={handleButtonClick}>
          <NavigateBeforeIcon />
          Back
        </Button>
        <Typography variant="h1" className="heading">Contact Information</Typography>
      </div>      <form onSubmit={handleSubmit} className="personal-info-wrap" autoComplete="off" data-form-type="other">
        <div style={{ display: 'none', height: 0, width: 0, opacity: 0 }}>
          <input type="text" id="prevent_phone" name="phone" autoComplete="off" />
          <input type="text" id="prevent_email" name="email" autoComplete="off" />
          <input type="text" id="prevent_name" name="name" autoComplete="off" />
        </div>
        <Typography variant="h2" className="heading-med">Personal Information</Typography>
        <div className="form-wrap">
          <FormField
            label="First Name"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
          />
          <FormField
            label="Last Name"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
          />
          <FormField
            label="State"
            name="state"
            value={formData.state}
            onChange={handleChange}
            error={errors.state}
          >
            <Select
              name="state"
              value={formData.state}
              onChange={handleChange}
              IconComponent={ArrowDropDownCircleIcon}
              className="custom-select"
              MenuProps={{
                PaperProps:{
                  className: "custom-dropdown-menu",
                }
              }}
            >
              <MenuItem value="">Select State</MenuItem>
              {US_STATES.map(state => (
                <MenuItem className="menu-item-hover" key={state?.name} value={state?.name}>{state?.name}</MenuItem>
              ))}
            </Select>
          </FormField>

          <FormField
            label="Zip Code"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            error={errors.zipCode}
          />
          <FormField
            label="Phone Number"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
            error={errors.phoneNumber}
          >            <Input
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              autoComplete="chrome-off"
              inputProps={{
                autoComplete: "chrome-off",
                'data-lpignore': "true",
                'data-form-type': "other",
                'autocorrect': "off",
                'autocapitalize': "off",
                'spellcheck': "false",
                maxLength: 12
              }}
            />
          </FormField>          <FormField
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          >
            <Input
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="chrome-off"
              inputProps={{
                autoComplete: "chrome-off",
                'data-lpignore': "true",
                'data-form-type': "other",
                'autocorrect': "off",
                'autocapitalize': "off",
                'spellcheck': "false",
                'data-autocompletetype': "none"
              }}
            />
          </FormField>
        </div>

        <div className="form-wrap-full">
          <FormField
            label="Are you Affiliated with the U.S. Military?"
            name="militaryAffiliation"
            value={formData.militaryAffiliation}
            onChange={handleChange}
            error={errors.militaryAffiliation}
          >
            <Select
              name="militaryAffiliation"
              value={formData.militaryAffiliation}
              onChange={handleChange}
              className="custom-select"
              IconComponent={ArrowDropDownCircleIcon}
            >
              <MenuItem className="menu-item-hover" value="Yes">Yes</MenuItem>
              <MenuItem className="menu-item-hover" value="No">No</MenuItem>
            </Select>
          </FormField>
        </div>

        <div className="form-wrap-full">
          <FormField
            label="Best Time to Reach You"
            name="bestTimeToReach"
            value={formData.bestTimeToReach}
            error={errors.bestTimeToReach}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                value={formData.bestTimeToReach}
                onChange={(newValue) => {
                  const currentDateTime = new Date();
                  if (newValue <= currentDateTime) {
                    setErrors(prev => ({
                      ...prev,
                      bestTimeToReach: "Please select a future date and time"
                    }));
                    return;
                  }
                  setFormData(prev => ({
                    ...prev,
                    bestTimeToReach: newValue
                  }));
                  setErrors(prev => ({ ...prev, bestTimeToReach: "" }));
                }}
                minDateTime={new Date()}
                shouldDisableTime={(timeValue, clockType) => {
                  const currentDateTime = new Date();
                  const selectedDate = new Date(formData.bestTimeToReach);
                  
                  if (selectedDate.toDateString() === currentDateTime.toDateString()) {
                    if (clockType === 'hours') {
                      return timeValue < currentDateTime.getHours();
                    }
                    if (clockType === 'minutes' && selectedDate.getHours() === currentDateTime.getHours()) {
                      return timeValue < currentDateTime.getMinutes();
                    }
                  }
                  return false;
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "standard",
                    error: !!errors.bestTimeToReach,
                    helperText: null,
                    placeholder: "MM/DD/YYYY hh:mm aa",
                    inputProps: {
                      readOnly: true,
                      style: { color: formData.bestTimeToReach ? 'inherit' : '#bdbdbd' }
                    },
                    sx: {
                      '& .MuiInput-underline:after': {
                        borderBottomColor: errors.bestTimeToReach ? '#d32f2f' : undefined,
                      },
                      '& .MuiInputAdornment-root': {
                        position: 'absolute',
                        right: '2%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      },
                      cursor: 'pointer',
                    },
                    onClick: (event) => {
                      event.currentTarget.querySelector('input').focus();
                      event.currentTarget.querySelector('button').click();
                    }
                  }
                }}
                format="MM/dd/yyyy hh:mm a"
                ampm
                disablePast
                closeOnSelect={false}
                PopperProps={{
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 10],
                      },
                    },
                    {
                      name: 'flip',
                      enabled: true,
                    },
                  ],
                }}
              />
            </LocalizationProvider>
          </FormField>
        </div>

        <Typography className="paragraph">
        By submitting this form, you agree to receive communications from The CDL School. You can unsubscribe from these communications at any time. For more information on how to unsubscribe, please review our Privacy Policy and Terms and Conditions.
        </Typography>

        <div className="btn-wrap">
          <Button type="submit" className="send-info-btn" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Request a Call'}
          </Button>
        </div>
      </form>

      <div className="help-box">
        <Typography className="heading">Need Help?</Typography>
        <Typography className="para">If you have any questions, call us +1785 98403 4 or contact customer@cdlschool.com</Typography>
      </div>
    </div>
  );
}

export default Contact;
